import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ErrorState,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Textarea,
  TOUCH,
} from "@nymbl/nos2-rules";
// src/components/time/SignOffDialog.tsx
import { useState } from "react";
import { periodLabel, RevokeSignOff } from "@/components/time/RevokeSignOff";
import { useSubmitSignOff } from "@/hooks/use-time-signoffs";
import { formatMonth, formatWeekRange, type IsoDate } from "@/lib/time/dates";
import { formatHours, normalizeComment } from "@/lib/time/rules";
import {
  isEnded,
  isLateNow,
  MONTH_NEEDS_WEEKS,
  missingHours,
  needsReason,
  openMonths,
  revocableRows,
  signableWeeks,
  signedOffMonthOf,
  signOffBlock,
} from "@/lib/time/signoff-rules";
import { cn } from "@/lib/utils";
import type { SignOffRow, SignOffView } from "@/schemas/time";

/** TimeEntrySignOff.Comment is nvarchar(254). */
const COMMENT_MAX = 254;

interface SignOffDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  view: SignOffView | undefined;
  error: boolean;
  onRetry: () => void;
  today: IsoDate;
  now: Date;
}

interface Choice {
  reasonId: string | null;
  comment: string;
}

export function SignOffDialog({
  open,
  onOpenChange,
  view,
  error,
  onRetry,
  today,
  now,
}: SignOffDialogProps) {
  const [choices, setChoices] = useState<Record<string, Choice>>({});
  const submit = useSubmitSignOff();
  const items: SignOffRow[] = view
    ? [...signableWeeks(view.rows, today), ...openMonths(view.rows, today)]
    : [];

  const choiceOf = (row: SignOffRow): Choice => choices[row.id] ?? { reasonId: null, comment: "" };
  const blockOf = (row: SignOffRow) =>
    view
      ? signOffBlock(
          row,
          { reasonId: choiceOf(row).reasonId, comment: normalizeComment(choiceOf(row).comment) },
          { isFullTime: view.isFullTime, reasons: view.reasons, today, rows: view.rows },
        )
      : "Loading";
  const setChoice = (row: SignOffRow, patch: Partial<Choice>) =>
    setChoices((c) => ({ ...c, [row.id]: { ...choiceOf(row), ...patch } }));
  // A signed-off row's draft reason and comment are dropped, so nothing stale lingers.
  const send = async (row: SignOffRow) => {
    await submit.mutateAsync({
      signOffId: row.id,
      reasonId: choiceOf(row).reasonId,
      comment: normalizeComment(choiceOf(row).comment),
    });
    setChoices((c) => Object.fromEntries(Object.entries(c).filter(([id]) => id !== row.id)));
  };

  // A week or month still in progress is signed off on its own: it locks days not lived yet.
  const readyRows = items.filter((r) => blockOf(r) === null && isEnded(r, today));

  // One after another, stopping at the first failure (the hook's toast shows why).
  async function signAllReady() {
    for (const row of readyRows) {
      const done = await send(row).then(
        () => true,
        () => false,
      );
      if (!done) return;
    }
  }

  const ready = readyRows.length;
  // TE-47: signed-off rows whose month is still open can be revoked, newest first.
  const revocable = view ? revocableRows(view.rows) : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange} dirty={Object.keys(choices).length > 0}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Sign off weeks</DialogTitle>
          <DialogDescription className="sr-only">
            Choose the weeks and months to sign off, including the current ones. A signed-off week
            or month can be revoked until its month closes.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="flex flex-col gap-4">
          {error && <ErrorState message="Sign-offs didn't load" onRetry={onRetry} />}
          {!error && !view && (
            <div className="space-y-3" aria-busy="true">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          )}
          {!error && view && items.length === 0 && (
            <p className="py-4 text-center font-semibold text-success">All signed off</p>
          )}
          {!error && items.length > 0 && (
            <div className="space-y-3">
              {items.map((row) => {
                const missing = missingHours(row);
                const late = isLateNow(row, now);
                const block = blockOf(row);
                const choice = choiceOf(row);
                const waitingOnWeeks = block === MONTH_NEEDS_WEEKS;
                const asksReason =
                  view && !waitingOnWeeks ? needsReason(row, view.isFullTime) : false;
                // Every control below names its period, so ten weeks never give ten identical
                // "Sign off" buttons to an agent (UI standard 12).
                const period =
                  row.periodType === "week"
                    ? `Week of ${formatWeekRange(row.start)}`
                    : formatMonth(row.start);
                return (
                  <div key={row.id} className="space-y-3 rounded-lg border p-3">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="font-semibold">{period}</div>
                        <div
                          className={cn("text-sm", late ? "text-danger" : "text-muted-foreground")}
                        >
                          {formatHours(row.totalHours)} hrs logged ·{" "}
                          {missing > 0 ? `${formatHours(missing)} hrs missing` : "complete"}
                          {late ? " · late" : ""}
                        </div>
                        {waitingOnWeeks ? (
                          <div className="text-sm text-muted-foreground">{MONTH_NEEDS_WEEKS}</div>
                        ) : (
                          !isEnded(row, today) && (
                            <div className="text-sm text-muted-foreground">
                              Still in progress. Signing off locks this {row.periodType}; revoke to
                              log more.
                            </div>
                          )
                        )}
                      </div>
                      <Button
                        type="button"
                        className={TOUCH}
                        disabled={block !== null || submit.isPending}
                        title={block ?? undefined}
                        aria-label={`Sign off ${period}`}
                        aria-describedby={block ? `block-${row.id}` : undefined}
                        onClick={() => {
                          void send(row).then(
                            () => undefined,
                            () => undefined,
                          );
                        }}
                      >
                        Sign off
                      </Button>
                      {block && (
                        <span id={`block-${row.id}`} className="sr-only">
                          {block}
                        </span>
                      )}
                    </div>
                    {asksReason && (
                      <div className="space-y-1">
                        <Label htmlFor={`reason-${row.id}`}>
                          Why are {formatHours(missing)} hrs missing?
                          <span className="sr-only"> ({period})</span>
                        </Label>
                        <Select
                          value={choice.reasonId ?? undefined}
                          onValueChange={(value) => setChoice(row, { reasonId: value })}
                        >
                          <SelectTrigger id={`reason-${row.id}`} className={cn("w-full", TOUCH)}>
                            <SelectValue placeholder="Choose a reason" />
                          </SelectTrigger>
                          <SelectContent>
                            {view?.reasons.map((r) => (
                              <SelectItem key={r.id} value={r.id}>
                                {r.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {/* Missing hours always need a comment, whatever the reason (Zack, 2026-10-15). */}
                    {asksReason && (
                      <div className="space-y-1">
                        <div className="flex items-baseline justify-between gap-2">
                          <Label htmlFor={`comment-${row.id}`}>
                            Comment<span className="sr-only"> ({period})</span>
                          </Label>
                          <span className="text-xs text-muted-foreground tabular-nums">
                            {choice.comment.length}/{COMMENT_MAX}
                          </span>
                        </div>
                        <Textarea
                          id={`comment-${row.id}`}
                          maxLength={COMMENT_MAX}
                          rows={2}
                          value={choice.comment}
                          onChange={(e) => setChoice(row, { comment: e.target.value })}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {!error && revocable.length > 0 && (
            <section aria-label="Signed off" className="space-y-2">
              <h3 className="text-sm font-semibold text-muted-foreground">Signed off</h3>
              <ul className="divide-y rounded-lg border">
                {revocable.map((row) => (
                  <li
                    key={row.id}
                    className="flex flex-wrap items-center justify-between gap-3 p-3"
                  >
                    <div>
                      <div className="font-semibold">
                        {row.periodType === "week"
                          ? `Week of ${periodLabel(row)}`
                          : periodLabel(row)}
                      </div>
                      <div
                        className={cn(
                          "text-sm",
                          row.isLate ? "text-danger" : "text-muted-foreground",
                        )}
                      >
                        {formatHours(row.totalHours)} hrs{row.isLate ? " · late" : ""}
                      </div>
                    </div>
                    <RevokeSignOff
                      row={row}
                      month={view ? signedOffMonthOf(row, view.rows) : null}
                      look="row"
                    />
                  </li>
                ))}
              </ul>
            </section>
          )}
        </DialogBody>
        <DialogFooter className="flex-wrap items-center justify-between gap-2 sm:justify-between">
          <span aria-hidden="true" />
          {/* A failed refetch keeps stale rows; never offer them while the list is hidden. */}
          {!error && items.length > 0 && (
            <Button
              type="button"
              className={TOUCH}
              disabled={ready === 0 || submit.isPending}
              onClick={() => {
                void signAllReady();
              }}
            >
              Sign off all ready weeks
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
